import { browserApiClient, requiredApiData } from './browser-api';
import { visualFixtureName } from './vertical-slice';

export interface ShopProduct {
  code: string;
  productType: string;
  entitlementCode: string;
  priceMinor: number;
  currency: string;
  benefits: readonly string[];
  competitiveAdvantage: boolean;
  externalValue: boolean;
}

export interface PlayerEntitlement {
  id: string;
  code: string;
  startsAt: string;
  endsAt?: string;
  status: string;
  competitiveEligible: boolean;
}

export interface CheckoutResult {
  purchaseId: string;
  productCode: string;
  status: string;
  checkoutUrl?: string;
}

export interface NativeStoreContext {
  provider: 'apple_app_store' | 'google_play';
  accountBinding: string;
  products: readonly {
    productCode: string;
    productType: string;
    providerProductRef: string;
    priceMinor: number;
    currency: string;
  }[];
}

const visualCatalog: readonly ShopProduct[] = [
  {
    code: 'explorer_pass_monthly',
    productType: 'subscription',
    entitlementCode: 'explorer_pass',
    priceMinor: 499,
    currency: 'EUR',
    benefits: ['Seasonal adornment track', 'Expanded route-planning slots', 'Monthly city banner'],
    competitiveAdvantage: false,
    externalValue: false,
  },
  {
    code: 'canal_banner_bundle',
    productType: 'cosmetic_bundle',
    entitlementCode: 'canal_banner_bundle',
    priceMinor: 299,
    currency: 'EUR',
    benefits: ['Three woven settlement banners', 'Two canoe paint patterns'],
    competitiveAdvantage: false,
    externalValue: false,
  },
];

export async function fetchShopCatalog(): Promise<readonly ShopProduct[]> {
  if (visualFixtureName() === 'pass') return visualCatalog;
  const client = browserApiClient();
  const response = await client.GET('/api/v1/shop/catalog');
  const data = requiredApiData(
    response.data,
    response.error,
    'The market catalogue is unavailable.',
  );
  return data.products.map((product) => ({
    code: product.code,
    productType: product.product_type,
    entitlementCode: product.entitlement_code,
    priceMinor: product.price_minor,
    currency: product.currency,
    benefits: product.benefits,
    competitiveAdvantage: product.competitive_advantage,
    externalValue: product.external_value,
  }));
}

export async function fetchEntitlements(): Promise<readonly PlayerEntitlement[]> {
  if (visualFixtureName() === 'pass') return [];
  const client = browserApiClient();
  const response = await client.GET('/api/v1/entitlements');
  const data = requiredApiData(response.data, response.error, 'Your entitlements are unavailable.');
  return data.entitlements.map((entitlement) => ({
    id: entitlement.id,
    code: entitlement.entitlement_code,
    startsAt: entitlement.starts_at,
    endsAt: entitlement.ends_at ?? undefined,
    status: entitlement.status,
    competitiveEligible: entitlement.competitive_eligible,
  }));
}

export async function beginCheckout(
  productCode: string,
  idempotencyKey: string,
): Promise<CheckoutResult> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST('/api/v1/payments/checkout', {
    params: { header: { 'Idempotency-Key': idempotencyKey } },
    body: { product_code: productCode },
  });
  const data = requiredApiData(response.data, response.error, 'Checkout could not be prepared.');
  return {
    purchaseId: data.purchase_id,
    productCode: data.product_code,
    status: data.status,
    checkoutUrl: data.checkout_url ?? undefined,
  };
}

export async function submitNativeReceipt(
  provider: 'apple_app_store' | 'google_play',
  productCode: string,
  receipt: string,
): Promise<CheckoutResult> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST('/api/v1/payments/receipts/{provider_code}', {
    params: { path: { provider_code: provider } },
    body: { product_code: productCode, receipt },
  });
  const data = requiredApiData(
    response.data,
    response.error,
    'The store purchase could not be verified.',
  );
  return {
    purchaseId: data.purchase_id,
    productCode: data.product_code,
    status: data.status,
  };
}

export async function fetchNativeStoreContext(
  provider: 'apple_app_store' | 'google_play',
): Promise<NativeStoreContext> {
  const client = browserApiClient();
  const response = await client.GET('/api/v1/payments/native-context/{provider_code}', {
    params: { path: { provider_code: provider } },
  });
  const data = requiredApiData(
    response.data,
    response.error,
    'Native store products are unavailable.',
  );
  return {
    provider,
    accountBinding: data.account_binding,
    products: data.products.map((product) => ({
      productCode: product.product_code,
      productType: product.product_type,
      providerProductRef: product.provider_product_ref,
      priceMinor: product.price_minor,
      currency: product.currency,
    })),
  };
}

export function safeCheckoutUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const target = new URL(value, window.location.origin);
    const sameOrigin = target.origin === window.location.origin;
    const stripeHosted =
      target.protocol === 'https:' &&
      target.hostname === 'checkout.stripe.com' &&
      target.port === '';
    if (!sameOrigin && !stripeHosted) return undefined;
    if (target.username || target.password) return undefined;
    return target.href;
  } catch {
    return undefined;
  }
}
